'use client';

import { Checkbox } from '@sgcmpteltd/uikit-react';
import cx from 'clsx';
import Link from 'next/link';
import { useMemo } from 'react';

import {
  ListTypeLabel,
// eslint-disable-next-line @stylistic/max-len
} from '@/app/(routes)/listing/_components/page-content/search-list/car-card/listing-type-label';
import { ImageWithFallback } from '@/components/image-with-fallback';
import { joinSgcmSite } from '@/utils/url/join/public';

import {
  IMAGE_HEIGHT,
  IMAGE_WIDTH,
  LIST_TYPE_LABEL_SIZE,
  SGCM_IMAGE_URL,
} from './constants';
import styles from './styles.module.scss';
import type { Component } from './types';

export const SubModelCompareContent: Component = ({
  adList,
  compareItems,
  onComparedCheckboxChange,
  submodels,
  id,
  make,
  model,
}) => {
  const sortedSubmodels = submodels
    .slice()
    .sort((a, b) => (a.price ?? 0) - (b.price ?? 0));

  const isComparedSubmodelArray = useMemo(() => {
    const comparedSubmodelArray = sortedSubmodels.map(
      submodel => compareItems.some(
        compareItem => compareItem.submodelId === submodel.id,
      ),
    );

    return comparedSubmodelArray;
  }, [compareItems, sortedSubmodels]);

  const handleCheckboxChange = (submodelId: number, index: number) => {
    onComparedCheckboxChange?.({
      id,
      submodelId,
      make,
      model,
      name: sortedSubmodels[index].name,
      thumbnailUrl: `https://i.i-sgcm.com/new_cars/cars/${id}/${id}_m.jpg`,
    }, !isComparedSubmodelArray[index]);
  };

  return (
    <main>
      <div className={styles.containerSubModelCompare}>
        <div className={styles.textTitle}>
          Select submodel of
          {' '}
          {make}
          {' '}
          {model}
          {' '}
          to
          {' '}
          <Link
            className={styles.textCompare}
            href={`${joinSgcmSite('/new_cars/newcars_compare.php')}`}>
            compare
          </Link>
          {' '}
          with another car(s)
        </div>
        <div className={styles.containerSubModel}>
          <div className={styles.carImage}>
            <ImageWithFallback
              alt={`${id}-image`}
              className={styles.imageVehicle}
              height={IMAGE_HEIGHT}
              src={`${SGCM_IMAGE_URL}/${id}/${id}_m.jpg`}
              width={IMAGE_WIDTH} />
          </div>
          <div className={styles.containerCheckBox}>
            { sortedSubmodels.map((subModel, idx) => {
              const checkboxId = `checkbox-${subModel.id}`;
              const links = [
                { href: `${joinSgcmSite(`/new_cars/newcars_specs.php?CarCode=${id}&Subcode=${subModel.id}`)}`, label: 'Specs' },
                { href: `${joinSgcmSite(`/new_cars/newcars_features.php?CarCode=${id}&Subcode=${subModel.id}`)}`, label: 'Features' },
              ];

              if (!adList) {
                links.push({ href: `${joinSgcmSite(`/new_cars/newcars_overview.php?CarCode=${id}&Subcode=${subModel.id}`)}`, label: 'Pricelist' });
              }
              return (
                <div
                  key={subModel.id}
                  className={styles.checkBoxContent}>
                  <div className={styles.containerMobileCheck}>
                    <Checkbox
                      checked={isComparedSubmodelArray[idx]}
                      id={checkboxId}
                      size={16}
                      value={subModel.id}
                      onChange={
                        () => handleCheckboxChange(subModel.id, idx)
                      } />
                  </div>
                  <div className={styles.containerName}>
                    <button
                      className={styles.textName}
                      onClick={
                        () => handleCheckboxChange(subModel.id, idx)
                      }>
                      {subModel.name}
                      {' '}
                      (
                      {subModel.transmissionType}
                      )
                    </button>
                    { subModel.isFacelift && (
                      <ListTypeLabel
                        labelSize={LIST_TYPE_LABEL_SIZE}
                        type="facelift" />
                    )}
                    { subModel.isNewVariant && (
                      <ListTypeLabel
                        labelSize={LIST_TYPE_LABEL_SIZE}
                        type="newSubmodel" />
                    )}
                  </div>
                  <div className={styles.containerTooltip}>
                    {links.map(({ href, label }, i) => (
                      <div key={`tooltip-${label}`}>
                        <a
                          href={href}
                          rel="noreferrer noopener"
                          target="_blank">
                          {label}
                        </a>
                        <span className={cx(
                          styles.linkSeparate,
                          i === links.length - 1 && styles.isLastLink, // @suppress longLineCheck
                        )}>
                          {i < links.length - 1 && '|'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </main>
  );
};
